/**
 * Question Index Mapper
 *
 * Provides flexible question matching for BRAG and DREAM sheets.
 * Maps questions to their category and index position to handle:
 * - Question text changes over time
 * - Legacy data with old question text
 * - Future question updates
 */

export interface QuestionMetadata {
  category: string;
  index: number;
  currentText: string;
}

/**
 * Get the category and index for a question from the current question set
 */
export function getQuestionMetadata(
  questionText: string,
  questionsConfig: Record<string, { questions: string[] }>
): QuestionMetadata | null {
  for (const [category, config] of Object.entries(questionsConfig)) {
    const index = config.questions.indexOf(questionText);
    if (index !== -1) {
      return {
        category,
        index,
        currentText: questionText
      };
    }
  }
  return null;
}

/**
 * Get current question text by category and index
 */
export function getQuestionByIndex(
  category: string,
  index: number,
  questionsConfig: Record<string, { questions: string[] }>
): string | null {
  const categoryConfig = questionsConfig[category];
  if (!categoryConfig || index < 0 || index >= categoryConfig.questions.length) {
    return null;
  }
  return categoryConfig.questions[index];
}

/**
 * Try to match a legacy question to a current question
 * Uses keyword matching to find the most likely match
 */
export function matchLegacyQuestion(
  legacyQuestion: string,
  questionsConfig: Record<string, { questions: string[] }>
): QuestionMetadata | null {
  const lowerLegacy = legacyQuestion.toLowerCase();

  // Define patterns for each category to help with matching
  // BRAG categories: B, R, A, G
  // DREAM categories: D, R, E, A, M
  // Note: Some letters overlap, so patterns must be distinctive
  const categoryPatterns: Record<string, string[]> = {
    // BRAG categories
    'B': ['challenge', 'overcome', 'obstacle', 'difficult', 'conflict', 'impossible', 'comfort zone', 'breakthrough'],
    'G': ['evolved', 'growth', 'developed', 'learning', 'feedback', 'leader', 'team member', 'skill', 'perspective'],
    // Shared categories - context dependent
    'R_BRAG': ['achievement', 'accomplishment', 'innovative', 'remarkable', 'results', 'surpassed', 'wow', 'exceeded', 'impressive'],
    'A_BRAG': ['applied', 'expertise', 'solve', 'measurable', 'impact', 'process', 'improvement', 'practical', 'solution', 'value'],
    // DREAM categories
    'D': ['dream', 'ideal', 'values', 'perfect day', 'legacy'],
    'E': ['envision', 'next', 'years', 'industries', 'future'],
    'M': ['motivates', 'momentum', 'committed'],
    'R_DREAM': ['resources', 'tools', 'relationships', 'learning opportunities', 'balance', 'compensation'],
    'A_DREAM': ['action', 'steps', 'networking', 'brand', 'timeline', 'measuring progress']
  };

  let bestMatch: QuestionMetadata | null = null;
  let bestScore = 0;

  // Determine if this is BRAG or DREAM based on available categories
  const isBRAG = 'B' in questionsConfig || 'G' in questionsConfig;
  const isDREAM = 'D' in questionsConfig || 'M' in questionsConfig;

  for (const [category, config] of Object.entries(questionsConfig)) {
    // Get patterns for this category, considering context
    let patterns = categoryPatterns[category] || [];

    // For shared categories, use context-specific patterns
    if (category === 'R') {
      patterns = isBRAG ? categoryPatterns['R_BRAG'] : categoryPatterns['R_DREAM'];
    } else if (category === 'A') {
      patterns = isBRAG ? categoryPatterns['A_BRAG'] : categoryPatterns['A_DREAM'];
    }

    config.questions.forEach((currentQuestion, index) => {
      const lowerCurrent = currentQuestion.toLowerCase();
      let score = 0;

      // Check for pattern matches
      patterns.forEach(pattern => {
        if (lowerLegacy.includes(pattern) && lowerCurrent.includes(pattern)) {
          score += 2;
        }
      });

      // Check for word overlap
      const legacyWords = lowerLegacy.split(/\W+/).filter(w => w.length > 3);
      const currentWords = lowerCurrent.split(/\W+/).filter(w => w.length > 3);

      legacyWords.forEach(word => {
        if (currentWords.includes(word)) {
          score += 1;
        }
      });

      // Bonus for similar length
      if (Math.abs(legacyQuestion.length - currentQuestion.length) < 20) {
        score += 1;
      }

      if (score > bestScore) {
        bestScore = score;
        bestMatch = {
          category,
          index,
          currentText: currentQuestion
        };
      }
    });
  }

  // Only return match if score is reasonable
  return bestScore >= 3 ? bestMatch : null;
}

/**
 * Create a stable key for storing responses
 * Uses category and index instead of question text
 */
export function getResponseKey(category: string, index: number): string {
  return `${category}_${index}`;
}

/**
 * Parse a response key back to category and index
 */
export function parseResponseKey(key: string): { category: string; index: number } | null {
  const match = key.match(/^([A-Z])_(\d+)$/);
  if (!match) return null;
  return {
    category: match[1],
    index: parseInt(match[2], 10)
  };
}

/**
 * Migrate legacy responses to use stable keys
 */
export function migrateLegacyResponses(
  legacyResponses: Record<string, string>,
  questionsConfig: Record<string, { questions: string[] }>
): Record<string, string> {
  const migratedResponses: Record<string, string> = {};

  for (const [questionText, response] of Object.entries(legacyResponses)) {
    // Try to find metadata for this question
    let metadata = getQuestionMetadata(questionText, questionsConfig);

    // If not found, try legacy matching
    if (!metadata) {
      metadata = matchLegacyQuestion(questionText, questionsConfig);
    }

    if (metadata) {
      const key = getResponseKey(metadata.category, metadata.index);
      migratedResponses[key] = response;
      console.log(`📦 Migrated response: "${questionText.substring(0, 50)}..." → ${key}`);
    } else {
      // Keep legacy key if we can't match it
      console.warn(`⚠️ Could not migrate question: "${questionText.substring(0, 50)}..."`);
      migratedResponses[questionText] = response;
    }
  }

  return migratedResponses;
}
